import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { useAuthContext } from '@/context/AuthContext.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import { TextField, Typography, Box, Stack } from '@mui/material';

const loginSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
  password: z.string().min(1, { message: 'This field is required.' }),
  remember_me: z.boolean().optional(),
});

type LoginSchema = z.infer<typeof loginSchema>;

const LoginPage: FC = () => {
  const { control, handleSubmit } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      remember_me: false,
    },
  });
  const { t } = useTranslation();
  const {
    loginQuery: { mutateAsync: login, isPending: isLoadingLogin, error: errorLogin, isError: isErrorLogin },
  } = useAuthContext();

  const handleLogin = (data: LoginSchema) => {
    void (async () => {
      try {
        if (isLoadingLogin) return;

        await login({
          email: data.email,
          password: data.password,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent className="w-screen h-screen bg-bg-light px-4 py-6">
      <Stack spacing={4} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter your credentials to access your account.
          </Typography>
        </Box>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(handleLogin)}>
          <Stack spacing={3}>
            {isErrorLogin && <Alert status="error">{errorLogin?.message}</Alert>}
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  value={value}
                  onChange={onChange}
                  placeholder={t('placeholder.email')}
                  variant="outlined"
                />
              )}
            />
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextField
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  value={value}
                  onChange={onChange}
                  type="password"
                  autoComplete="current-password"
                  placeholder={t('placeholder.password')}
                  variant="outlined"
                />
              )}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Controller
                  control={control}
                  name="remember_me"
                  render={({ field: { onChange, value } }) => (
                    <>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={onChange}
                        className="checkbox checkbox-primary bg-white border-2 checked:border-[#4E7DF6] checked:bg-[#4E7DF6] hover:checked:bg-[#3D63C4] checked:checkbox-[#4E7DF6]"
                        style={{ '--chkbg': '#4E7DF6', '--chkfg': 'white' } as React.CSSProperties}
                      />
                      <Typography variant="body2" color="text.primary">
                        {t('label.rememberMe')}
                      </Typography>
                    </>
                  )}
                />
              </Box>
              <Link to={Routes.FORGOT_PASSWORD}>
                <Button color="ghost" size="sm">
                  {t('button.forgotPassword')}?
                </Button>
              </Link>
            </Box>
            <Button
              color="primary"
              className="btn-primary w-full rounded-full normal-case bg-[#4E7DF6] hover:bg-[#3D63C4] border-none"
              type="submit"
              disabled={isLoadingLogin}
            >
              {t('button.login')}
            </Button>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Link to={Routes.REGISTER}>
              <Button color="ghost">{t('button.createNewAccount')}</Button>
            </Link>
          </Box>
        </form>
      </Stack>
    </BrandCardSideContent>
  );
};

export default LoginPage;
